import { useCallback, useEffect } from "react";
import useToggle from "./useToggle";
import { logtoClient } from "../service/Logto";
import { useDispatch } from "react-redux";
import RouteNames from "../routes/type";
import { useNavigate } from "react-router";
import { setUserInfo } from "../store/user/actions";
import * as API from "../service/api";

export default function useAuthentication() {
  const {
    value: isLoading,
    setTrue: startLoader,
    setFalse: stopLoader,
  } = useToggle(true);
  const { value: isAuthenticated, setValue: setIsAuthenticated } =
    useToggle(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const checkAuthentication = useCallback(async () => {
    startLoader();
    try {
      const data = await logtoClient.isAuthenticated();
      if (data) {
        const userData = await logtoClient.fetchUserInfo();
        if (
          userData?.custom_data?.["dealer_portal_access"] &&
          userData?.organizations?.length > 0
        ) {
          const orgId = userData["organizations"][0];
          const orgData = await API.user.getOrgInfo(orgId);
          const orgTokenClaims =
            await logtoClient.getOrganizationTokenClaims(orgId);
          const isLegacyDealer = !!orgData?.["customData"]?.["legacy"];

          const rawAssociatedDealers =
            orgData?.["customData"]?.["associated_dealers"] || [];
          const defaultDealerId = orgData?.["customData"]?.["dealer_id"] || "";
          const defaultDealerName =
            orgData?.["customData"]?.["dealer_name"] || orgData["name"];
          const chatbotEnabled = orgData?.["customData"]?.["chatbot"] || false;
          const consolidatedDealers = [
            ...rawAssociatedDealers.map((d: any) => {
              const { dealer_id, ...rest } = d;
              return {
                id: d.id || dealer_id,
                ...rest,
              };
            }),
          ];
          if (
            defaultDealerId &&
            !consolidatedDealers.find((d: any) => d.id === defaultDealerId)
          ) {
            consolidatedDealers.unshift({
              id: defaultDealerId,
              alias: defaultDealerName,
            });
          }

          dispatch(
            setUserInfo({
              ...userData,
              selectedOrg: {
                id: orgData["id"],
                logo: orgData?.["customData"]?.["dealer_logo"] || "",
                name: defaultDealerName,
                permissions: isLegacyDealer
                  ? [
                      "read:contract",
                      "read:pending-contract",
                      "create:ticket",
                      "read:claims",
                      "cancel:contract",
                      ...(orgData?.["customData"]?.[
                        "dealer_portal_bulk_contract_access"
                      ]
                        ? ["create:bulk-contract"]
                        : []),
                    ]
                  : orgTokenClaims.scope.split(" "),
                can_file_claim:
                  orgData?.["customData"]?.["dealer_portal_can_file_claim"] ||
                  false,
                can_cancel_plan:
                  orgData?.["customData"]?.["dealer_portal_can_cancel_plan"] ??
                  true,
                associated_dealers: consolidatedDealers,
                chatbot_enabled: chatbotEnabled,
              },
            })
          );

          setIsAuthenticated(true);
        } else {
          navigate(RouteNames.UNAUTHORIZED);
        }
      }
    } catch (error) {
      console.error("Authentication error:", error);
    } finally {
      stopLoader();
    }
  }, [dispatch, navigate, setIsAuthenticated, startLoader, stopLoader]);

  useEffect(() => {
    checkAuthentication();
  }, [checkAuthentication]);

  return {
    isLoading,
    isAuthenticated,
  };
}
